import { useMemo, useState } from "react";
import { Home, ChevronDown, ChevronRight } from "lucide-react";
import { money, priceOrDash } from "./helpers";

/**
 * Standalone Smart Estimator services picker.
 * Groups services by tenant `service_categories` in the same order/labels as
 * the Services page and the New Estimate picker (Iter41 Phase 2). Services
 * without a `service_category_id` fall into a trailing "Uncategorized"
 * bucket so nothing disappears from the picker.
 *
 * Null / unconfigured `default_price` renders as an em-dash instead of $0.
 */
export default function ServicesCatalog({ services, categories = [], onPick }) {
  const activeServices = useMemo(
    () => (services || []).filter((s) => s.active !== false),
    [services],
  );

  const grouped = useMemo(() => {
    const groups = [];
    (categories || []).forEach((c) => {
      if (c.active === false) return;
      const items = activeServices.filter((s) => s.service_category_id === c.id);
      if (items.length > 0) groups.push({ id: c.id, name: c.name, items });
    });
    const uncat = activeServices.filter((s) => !s.service_category_id);
    if (uncat.length > 0) groups.push({ id: "uncategorized", name: "Uncategorized", items: uncat });
    return groups;
  }, [activeServices, categories]);

  // Default: all groups expanded so behavior matches the previous flat list.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderPrice = (s) => {
    if (s.estimator_config?.unit_price) {
      return money(s.estimator_config.unit_price) + `/${s.unit || "unit"}`;
    }
    return priceOrDash(s.default_price);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 card-crisp">
      <div className="flex items-center gap-2 mb-3">
        <Home className="w-4 h-4 text-[#FF5A00]" />
        <div className="font-heading font-bold">Services catalog</div>
      </div>
      <div className="text-xs text-zinc-500 mb-2">Click a service to add it as a line.</div>
      <div className="max-h-96 overflow-y-auto space-y-3" data-testid="services-picker">
        {grouped.length === 0 && (
          <div className="text-sm text-zinc-500 text-center py-4" data-testid="services-picker-empty">
            No services yet. Add services from Settings or the Services page.
          </div>
        )}
        {grouped.map((g) => {
          const isCollapsed = collapsed.has(g.id);
          return (
            <div key={g.id} data-testid={`services-picker-group-${g.id}`}>
              <button
                type="button"
                onClick={() => toggle(g.id)}
                className="w-full flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent mb-1 hover:text-zinc-900"
                data-testid={`services-picker-group-toggle-${g.id}`}
              >
                {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                <span className="truncate">{g.name}</span>
                <span className="ml-auto text-zinc-400">{g.items.length}</span>
              </button>
              {!isCollapsed && (
                <div className="space-y-1">
                  {g.items.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => onPick(s)}
                      className="w-full text-left border border-zinc-200 rounded-lg p-2 hover:border-[#FF5A00] hover:bg-orange-50 transition-colors"
                      data-testid={`service-pick-${s.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium truncate">{s.name}</div>
                        <div className="text-xs font-mono-accent text-zinc-700">
                          {renderPrice(s)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
